import { Card, Button, theme, Row, Col, Collapse, Tag, Typography } from "antd";
import { useMemo, useState, ReactElement } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { VzblTag } from "../../components/VzblTag";
import {
  SiderLabelValue,
  SiderLabelValueProps,
} from "../../components/SiderLabelValue";
import { EntityTypes } from "@vzbl/integrations";
import { MetadataTag, IndicatorType } from "src/components/MetadataTag";
import { UserAvatar } from "src/components/UserAvatar";
import { formatDuration } from "src/utils/duration";
import { ScoreIndicator } from "src/components/ScoreIndicator";

dayjs.extend(duration);

// Helper function to detect property type and common patterns
const detectPropertyType = (
  key: string,
  value: any
): PropertyValueType | "array" | "object" | "basic" => {
  // Check key patterns
  const keyLower = key.toLowerCase();
  // Normalize key by removing spaces and dots
  const normalizedKey = keyLower.replace(/[\s.]+/g, "");

  // Check for classification, urgency, and score patterns first
  if (
    keyLower === "vzblclassification" ||
    keyLower.endsWith("classification")
  ) {
    return "classification";
  }

  if (keyLower === "vzblurgency" || keyLower.endsWith("urgency")) {
    return "urgency";
  }

  if (
    keyLower.includes("score") ||
    keyLower.includes("clarity") ||
    keyLower.includes("friction")
  ) {
    return "score";
  }

  // First check for date patterns as they should take precedence
  if (
    keyLower.endsWith("date") ||
    keyLower.endsWith("at") ||
    keyLower.endsWith("on") ||
    ["created", "updated", "resolved", "completed", "closed", "started"].some(
      (d) => keyLower.startsWith(d)
    )
  ) {
    return "date";
  }

  // Time durations (assuming milliseconds)
  const durationPatterns = [
    "cycletime",
    "leadtime",
    "reviewtime",
    "resolutiontime",
    "timespent",
    "timeestimate",
    "duration",
    "elapsed",
    "age",
  ];

  if (
    durationPatterns.some((pattern) => normalizedKey.includes(pattern)) ||
    normalizedKey.endsWith("time")
  ) {
    return "duration";
  }

  if (typeof value === "number" && value > 1000 * 60) {
    return "duration";
  }

  if (
    !Number.isNaN(Number.parseFloat(value)) &&
    Number.parseFloat(value) > 1000 * 60
  ) {
    return "duration";
  }

  // Person fields - check for person objects with specific properties
  if (
    value &&
    typeof value === "object" &&
    (value.avatarUrl || value.name || value.uuid) &&
    !Array.isArray(value)
  ) {
    return "person";
  }

  // Team/Group arrays
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value[0] &&
    typeof value[0] === "object" &&
    (normalizedKey.includes("team") || normalizedKey.includes("group"))
  ) {
    return "tag";
  }

  // Sprint arrays
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value[0] &&
    typeof value[0] === "object" &&
    normalizedKey.includes("sprint")
  ) {
    return "tag";
  }

  // Status fields - more precise matching to avoid false positives
  if (
    keyLower === "status" ||
    keyLower.startsWith("status.") ||
    keyLower.endsWith(".status") ||
    (typeof value === "string" &&
      ["open", "closed", "in progress", "done"].includes(value.toLowerCase()))
  ) {
    return "status";
  }

  // Type fields
  if (keyLower.endsWith("type")) {
    return "type";
  }

  // Priority fields
  if (keyLower.includes("priority") || keyLower.includes("severity")) {
    return "priority";
  }

  // Links and references
  if (
    keyLower.includes("url") ||
    keyLower.includes("link") ||
    (typeof value === "string" &&
      (value.startsWith("http://") || value.startsWith("https://"))) ||
    (typeof value === "object" && value?.uuid && (value?.name || value?.title))
  ) {
    return "link";
  }

  // Check value types
  if (Array.isArray(value)) {
    return "array";
  }
  if (typeof value === "boolean") {
    return "boolean";
  }
  if (
    typeof value === "number" ||
    (Number.isFinite(value) && !Number.isNaN(Number.parseFloat(value)))
  ) {
    return "number";
  }
  if (value !== null && typeof value === "object") {
    return "object";
  }

  return "text";
};

// Property value types
export type PropertyValueType =
  | "status" // For status fields that should use MetadataTag
  | "type" // For type fields that should use TypeIndicator
  | "priority" // For priority fields that should use PriorityIndicator
  | "person" // For user/person fields
  | "date" // For date/timestamp fields
  | "duration" // For time duration fields
  | "link" // For URL fields
  | "tag" // For fields that should be displayed as tags
  | "text" // For plain text fields
  | "markdown" // For markdown content
  | "number" // For numeric values
  | "boolean" // For true/false values
  | "classification" // For issue/PR classification
  | "urgency" // For urgency levels
  | "score" // For numerical scores
  | "metadata"; // For metadata fields that should use MetadataTag

export interface PropertyDisplayRule {
  priority: number; // Higher number means higher priority
  showIfEmpty?: boolean; // Whether to show this property even if it's empty/null/undefined
  displayName?: string; // Optional custom display name
  category?: string; // Optional override for property category
  valueType?: PropertyValueType; // Type of the value for formatting
  formatValue?: (value: any) => string | ReactElement; // Optional custom formatter
  tooltip?: string | ((properties: EntityProperties) => string); // Static tooltip or function to generate tooltip
}

export interface EntityProperties extends Record<string, any> {
  [key: string]: any;
}

export interface DynamicPropertiesProps {
  properties: EntityProperties;
  orgId: string;
  context: any;
  entityType: keyof typeof EntityTypes;
  maxInitialProperties?: number;
  hiddenFields?: string[];
  categories?: {
    name: string;
    patterns: string[];
    priority: number;
  }[];
  showGroupHeaders?: boolean;
  onPropertyClick?: (key: string, value: any) => void;
  layout?: "split" | "stacked";
  displayRules?: Record<string, PropertyDisplayRule>;
  showMoreButton?: boolean;
}

// Default categories configuration
const defaultCategories = [
  {
    name: "Process",
    patterns: ["vzbl", "time"],
    priority: 1000,
  },
  {
    name: "People",
    patterns: ["assignee", "reporter", "author", "creator", "team", "by"],
    priority: 800,
  },
  {
    name: "Workflow",
    patterns: [
      "status",
      "type",
      "priority",
      "workflow",
      "sprint",
      "date",
      "at",
    ],
    priority: 700,
  },
  {
    name: "Metadata",
    patterns: [],
    priority: 0,
  },
];

// Default display rules for common properties
const defaultDisplayRules: Record<string, PropertyDisplayRule> = {
  vzblClassification: {
    priority: 90,
    valueType: "classification",
    displayName: "Classification",
    showIfEmpty: false,
  },
  vzblUrgency: {
    priority: 85,
    valueType: "urgency",
    displayName: "Urgency",
    showIfEmpty: false,
  },
  vzblClarityScore: {
    priority: 80,
    valueType: "score",
    displayName: "Clarity Score",
    showIfEmpty: false,
  },
  vzblFrictionScore: {
    priority: 75,
    valueType: "score",
    displayName: "Friction Score",
    showIfEmpty: false,
  },
  status: {
    priority: 100,
    valueType: "status",
    showIfEmpty: false,
  },
  issueType: {
    priority: 95,
    valueType: "type",
    displayName: "Type",
    showIfEmpty: false,
  },
  priority: {
    priority: 90,
    valueType: "priority",
    showIfEmpty: false,
  },
};

export const DynamicProperties: React.FC<DynamicPropertiesProps> = ({
  properties,
  orgId,
  context,
  entityType,
  maxInitialProperties = 5,
  hiddenFields = [],
  categories = [],
  showGroupHeaders = true,
  onPropertyClick,
  layout = "split",
  displayRules = {},
  showMoreButton = true,
}) => {
  const [showAll, setShowAll] = useState(false);
  const { token } = theme.useToken();

  // Move excludedProperties into useMemo
  const excludedProperties = useMemo(
    () => new Set(["uuid", "description", "title", ...hiddenFields]),
    [hiddenFields]
  );

  // Merge custom categories with defaults
  const mergedCategories = useMemo(() => {
    const categoryMap = new Map(
      defaultCategories.map((cat) => [cat.name, { ...cat }])
    );

    // Override or add new categories from custom config
    categories.forEach((customCat) => {
      const existing = categoryMap.get(customCat.name);
      if (existing) {
        // Merge patterns and override priority
        existing.patterns = [
          ...new Set([...existing.patterns, ...customCat.patterns]),
        ];
        existing.priority = customCat.priority;
      } else {
        categoryMap.set(customCat.name, { ...customCat });
      }
    });

    return [...categoryMap.values()].sort((a, b) => b.priority - a.priority);
  }, [categories]);

  // First, get all properties sorted by priority
  const allProperties = useMemo(() => {
    // Merge default display rules with provided rules inside useMemo
    const mergedDisplayRules = {
      ...defaultDisplayRules,
      ...displayRules,
    };

    const props: [string, any, PropertyDisplayRule | undefined][] = [];

    Object.entries(properties).forEach(([key, value]) => {
      // Skip excluded properties
      if (excludedProperties.has(key)) {
        return;
      }

      const displayRule = mergedDisplayRules[key];

      // For duration fields, ensure the value is treated as a number
      if (
        displayRule?.valueType === "duration" &&
        value !== undefined &&
        typeof value === "string"
      ) {
        value = Number.parseFloat(value);
      }

      // Skip empty values unless showIfEmpty is true
      if (
        (value === undefined || value === null || value === "") &&
        !displayRule?.showIfEmpty
      ) {
        return;
      }

      // Skip zero values for numeric fields unless showIfEmpty is true
      if (
        typeof value === "number" &&
        value === 0 &&
        !displayRule?.showIfEmpty
      ) {
        return;
      }

      // Skip empty arrays unless showIfEmpty is true
      if (
        Array.isArray(value) &&
        value.length === 0 &&
        !displayRule?.showIfEmpty
      ) {
        return;
      }

      props.push([key, value, displayRule]);
    });

    // Immediately return sorted props
    return props.sort((a, b) => {
      const ruleA = a[2];
      const ruleB = b[2];

      // If both have display rules, compare their priorities
      if (ruleA?.priority !== undefined && ruleB?.priority !== undefined) {
        return ruleB.priority - ruleA.priority;
      }

      // If only one has a priority, it takes precedence
      if (ruleA?.priority !== undefined) return -1;
      if (ruleB?.priority !== undefined) return 1;

      // If neither has a priority, sort alphabetically
      return a[0].localeCompare(b[0]);
    });
  }, [properties, excludedProperties, displayRules]);

  // Split into top properties and remaining properties based on priority threshold
  const topProperties = useMemo(() => {
    // Get the minimum priority for top properties
    const minTopPriority = 500;

    return allProperties
      .filter(
        ([_, __, rule]) =>
          rule?.priority !== undefined && rule.priority >= minTopPriority
      )
      .slice(0, maxInitialProperties);
  }, [allProperties, maxInitialProperties]);

  // Group remaining properties by category
  const groupedProperties = useMemo(() => {
    // Get properties not shown in top section
    const topPropertyKeys = new Set(topProperties.map(([key]) => key));
    const remainingProps = allProperties.filter(
      ([key]) => topPropertyKeys.has(key) === false
    );
    const groups: Record<
      string,
      [string, any, PropertyDisplayRule | undefined][]
    > = Object.fromEntries(mergedCategories.map((cat) => [cat.name, []]));

    remainingProps.forEach(([key, value, displayRule]) => {
      // Determine category - use display rule category if specified
      let matchedCategory = displayRule?.category || "Metadata";
      if (!displayRule?.category) {
        for (const category of mergedCategories) {
          if (
            category.patterns.some((pattern) =>
              key.toLowerCase().includes(pattern.toLowerCase())
            )
          ) {
            matchedCategory = category.name;
            break;
          }
        }
      }

      groups[matchedCategory] = groups[matchedCategory] || [];
      groups[matchedCategory].push([key, value, displayRule]);
    });

    // Remove empty groups and sort by category priority
    return Object.fromEntries(
      Object.entries(groups).filter(([_, props]) => props.length > 0)
    );
  }, [allProperties, mergedCategories, topProperties]);

  const handlePropertyClick = (key: string, value: any) => {
    if (onPropertyClick) {
      onPropertyClick(key, value);
    }
  };

  // Update the SiderLabelValue props to accept ReactElement
  interface ExtendedSiderLabelValueProps
    extends Omit<SiderLabelValueProps, "value"> {
    value?: string | ReactElement;
  }

  const renderProperty = ([key, value, displayRule]: [
    string,
    any,
    PropertyDisplayRule | undefined
  ]) => {
    let displayValue: string | ReactElement;
    if (displayRule?.formatValue) {
      displayValue = displayRule.formatValue(value);
    } else if (
      key.startsWith("vzbl") &&
      displayRule?.valueType !== "duration" &&
      displayRule?.valueType !== "classification" &&
      displayRule?.valueType !== "urgency" &&
      displayRule?.valueType !== "score"
    ) {
      // Only use VzblTag if it's not a special indicator type

      displayValue = (
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={value}
          linkParams={{
            orgId,
            entityType: EntityTypes,
            attribute: key,
            value,
          }}
        />
      );
    } else {
      const detectedType =
        displayRule?.valueType || detectPropertyType(key, value);

      // Only pass the type if it's a valid PropertyValueType
      const valueType = [
        "status",
        "type",
        "priority",
        "person",
        "date",
        "duration",
        "link",
        "tag",
        "text",
        "markdown",
        "number",
        "boolean",
        "classification",
        "urgency",
        "score",
      ].includes(detectedType)
        ? (detectedType as PropertyValueType)
        : undefined;
      displayValue = getReadableValue(key, value, valueType);
    }

    const valueString =
      typeof displayValue === "string" ? displayValue : value?.toString() || "";

    // Generate tooltip content
    let tooltipContent = valueString;
    if (displayRule?.tooltip) {
      tooltipContent =
        typeof displayRule.tooltip === "function"
          ? displayRule.tooltip(properties)
          : displayRule.tooltip;
    } else if (
      displayRule?.valueType === "person" ||
      detectPropertyType(key, value) === "person"
    ) {
      // For person fields, show name in tooltip
      if (Array.isArray(value)) {
        tooltipContent = value
          .map(
            (person) => person.name || person.displayName || person.toString()
          )
          .join(", ");
      } else if (typeof value === "object") {
        tooltipContent = value.name || value.displayName || value.toString();
      } else {
        tooltipContent = value.toString();
      }
    }

    return (
      <SiderLabelValue
        key={key}
        label={displayRule?.displayName || getPropertyDisplayName(key)}
        value={displayValue}
        onClick={() => handlePropertyClick(key, value)}
        tooltip={tooltipContent}
        ellipsis
      />
    );
  };

  const renderPropertyGroup = (
    category: string,
    props: [string, any, PropertyDisplayRule | undefined][]
  ) => (
    <Collapse.Panel key={category} header={category}>
      {props.map(renderProperty)}
    </Collapse.Panel>
  );

  const moreButton = showMoreButton &&
    allProperties.length > maxInitialProperties && (
      <div
        style={{
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: "2px 12px",
          marginTop: "auto",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="link"
          size="small"
          onClick={() => setShowAll(!showAll)}
          style={{ padding: 0 }}
        >
          {showAll
            ? "Show Less"
            : `Show ${allProperties.length - maxInitialProperties} More`}
        </Button>
      </div>
    );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: moreButton ? 0 : 12,
        }}
      >
        {/* Top priority properties with subtle grouping */}
        <div style={{ padding: "0 8px" }}>
          {(() => {
            // Group top properties by category
            const topGroups: Record<
              string,
              [string, any, PropertyDisplayRule | undefined][]
            > = {};

            topProperties.forEach((prop) => {
              const category = prop[2]?.category || "Other";
              if (!topGroups[category]) {
                topGroups[category] = [];
              }
              topGroups[category].push(prop);
            });

            return Object.entries(topGroups).map(([category, props]) => (
              <div key={category} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    backgroundColor: token.colorFillQuaternary,
                    padding: "4px 8px",
                    marginBottom: 4,
                    borderRadius: token.borderRadiusSM,
                  }}
                >
                  <Typography.Text
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 600,
                      color: token.colorTextSecondary,
                    }}
                  >
                    {category}
                  </Typography.Text>
                </div>
                {props.map(renderProperty)}
              </div>
            ));
          })()}
        </div>

        {/* Remaining properties in collapsible groups */}
        {showAll && (
          <Collapse ghost style={{ background: "transparent" }}>
            {Object.entries(groupedProperties).map(([category, props]) =>
              renderPropertyGroup(category, props)
            )}
          </Collapse>
        )}
      </div>
      {moreButton}
    </div>
  );
};

// Update the getReadableValue function
const getReadableValue = (
  key: string,
  value: any,
  valueType?: PropertyValueType
): string | ReactElement => {
  if (!value) return "-";

  switch (valueType) {
    case "classification": {
      return (
        <MetadataTag
          metadata={value}
          type={IndicatorType.classification}
          showLabel
          size="small"
        />
      );
    }

    case "urgency": {
      return (
        <MetadataTag
          metadata={value}
          type={IndicatorType.urgency}
          showLabel
          size="small"
        />
      );
    }

    case "score": {
      const numValue =
        typeof value === "string" ? Number.parseFloat(value) : value;
      return <ScoreIndicator value={numValue} showLabel size="small" />;
    }

    case "status": {
      return (
        <MetadataTag
          metadata={value}
          type={IndicatorType.status}
          showLabel
          size="small"
        />
      );
    }

    case "type": {
      return (
        <MetadataTag
          metadata={value}
          type={IndicatorType.issuetype}
          showLabel
          size="small"
        />
      );
    }

    case "priority": {
      return (
        <MetadataTag
          metadata={value}
          type={IndicatorType.priority}
          showLabel
          size="small"
        />
      );
    }

    case "person": {
      if (Array.isArray(value)) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {value.map((person, index) => {
              const name =
                person.name || person.displayName || person.value || "Unknown";
              return (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <UserAvatar user={person} size="small" showTooltip={false} />
                  <span>{name}</span>
                  {index < value.length - 1 && <span>,</span>}
                </div>
              );
            })}
          </div>
        );
      }
      if (typeof value === "object") {
        const name =
          value.name || value.displayName || value.value || "Unknown";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserAvatar user={value} size="small" showTooltip={false} />
          </div>
        );
      }
      return value.toString();
    }

    case "date": {
      try {
        const date = dayjs(value);
        if (date.isValid()) {
          return date.format("MMM D, YYYY");
        }
        return value.toString();
      } catch {
        return value.toString();
      }
    }

    case "duration": {
      let numericValue: number;
      if (typeof value === "string") {
        numericValue = Number.parseFloat(value);
      } else if (typeof value === "number") {
        numericValue = value;
      } else {
        return value.toString();
      }

      if (!Number.isNaN(numericValue)) {
        const keyLower = key.toLowerCase();
        const isLikelySeconds =
          numericValue < 1e10 ||
          keyLower.includes("timespent") ||
          keyLower.includes("estimate") ||
          keyLower.includes("cycletime") ||
          keyLower.includes("leadtime") ||
          keyLower.includes("resolutiontime");

        const valueInMs = isLikelySeconds ? numericValue * 1000 : numericValue;
        return formatDuration(valueInMs, { style: "human" });
      }
      return value.toString();
    }

    case "link": {
      if (
        typeof value === "string" &&
        (value.startsWith("http://") || value.startsWith("https://"))
      ) {
        return (
          <Typography.Link href={value} target="_blank">
            {value}
          </Typography.Link>
        );
      } else if (typeof value === "object" && value?.uuid) {
        const displayText =
          value.name || value.title || value.value || value.uuid;
        return <span>{displayText}</span>;
      }
      return value.toString();
    }

    case "tag": {
      if (Array.isArray(value)) {
        return (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {value.map((item, index) => (
              <Tag key={index}>
                {item.name || item.value || item.toString()}
              </Tag>
            ))}
          </div>
        );
      }
      return <Tag>{value}</Tag>;
    }

    case "markdown": {
      return <Typography.Text>{value}</Typography.Text>;
    }

    case "number": {
      const num = Number.parseFloat(value);
      if (Number.isNaN(num)) {
        return value.toString();
      }
      return num.toLocaleString();
    }

    case "boolean": {
      return value ? "Yes" : "No";
    }

    default: {
      return value.toString();
    }
  }
};

// Export for use in other components
export { getReadableValue };

export const formatPropertyValue = (
  key: string,
  value: any
): string | ReactElement => {
  return getReadableValue(key, value);
};

// Helper function to get property display name
export const getPropertyDisplayName = (key: string): string => {
  // Remove common prefixes
  let displayName = key.replace(/^vzbl/, "");

  // Split on camelCase
  displayName = displayName.replace(/([A-Z])/g, " $1");

  // Capitalize first letter
  return displayName.charAt(0).toUpperCase() + displayName.slice(1);
};

// Update the map function to include type
const renderArrayItems = (items: any[]): string => {
  if (items.length === 0) {
    return "";
  }

  return items
    .map((item: any) => {
      // Common object property patterns
      const commonProps = [
        "title",
        "name",
        "label",
        "displayName", // Display properties
        "key",
        "id",
        "value", // Identifier properties
        "summary",
        "description", // Content properties
      ];

      for (const prop of commonProps) {
        if (item[prop] !== undefined) {
          return item[prop].toString();
        }
      }

      // If no common properties found, try to get first non-object value
      const firstValue = Object.values(item).find(
        (v) => typeof v === "string" || typeof v === "number" || v === null
      );
      return firstValue === undefined ? "[Complex Value]" : String(firstValue);
    })
    .join(", ");
};
